/** @format */

import {
	Component,
	computed,
	inject,
	Input,
	input,
	OnDestroy,
	OnInit,
	signal
} from '@angular/core';
import {
	addProductReviewsMultimedia,
	InvalidResponse,
	ReviewFilter,
	ReviewPagination,
	ReviewService,
	ReviewsPaginationReturn,
	ReviewSummary,
	SelectedProductReviewsProsCons,
	selectProductReviewProductIdentifier,
	selectProductReviews,
	selectProductReviewSummary,
	selectReviewFilters,
	selectSearchFilter,
	selectSelectedFeature,
	selectSortFilter,
	setProductReviews
} from '@product-reviews/data-access';
import { ReviewAISummaryComponent } from '../review-ai-summary/review-ai-summary.component';
import { ReviewAiProsConsComponent } from '../review-ai-pros-cons/review-ai-pros-cons.component';
import { ReviewDrawerListComponent } from '../review-drawer-list/review-drawer-list.component';
import { ReviewFilterDrawerComponent } from '../review-filter-drawer/review-filter-drawer.component';
import { ReviewLoadMoreButtonComponent } from '../review-load-more-button/review-load-more-button.component';
import { ReviewResponsiveRatingSummaryComponent } from '../review-responsive-rating-summary/review-responsive-rating-summary.component';
import { ReviewhighlightCarouselComponent } from '../review-carousel/review-carousel.component';
import {
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService,
	homefieldIconStarFilled,
	homefieldIconStarHalf,
	homefieldIconStarOutlined
} from '@dsg-tech/homefield-angular-icons';
import { combineLatest, finalize, Observable, of, Subject } from 'rxjs';
import { debounceTime, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { MediaSourceEnum } from '../models';
import { Store } from '@ngrx/store';
import { selectKillswitch } from '@pdp/data-access/state';

/** @format */

@Component({
	selector: 'product-reviews-drawer-container',
	templateUrl: './reviews-drawer-container.component.html',
	styleUrl: './reviews-drawer-container.component.scss',
	imports: [
		HomefieldIconLibraryModule,
		ReviewAiProsConsComponent,
		ReviewAISummaryComponent,
		ReviewhighlightCarouselComponent,
		ReviewDrawerListComponent,
		ReviewFilterDrawerComponent,
		ReviewLoadMoreButtonComponent,
		ReviewResponsiveRatingSummaryComponent
	]
})
export class ReviewsDrawerContainerComponent implements OnInit, OnDestroy {
	private readonly store = inject(Store);
	private readonly reviewService = inject(ReviewService);
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

	@Input() isFirstPanelOpen;

	// Use values from BV if null
	readonly ratingCount = input<number | null>(null);
	readonly ratingValue = input<number | null>(null);

	private UNSUBSCRIBE$ = new Subject<void>();
	writeAReviewLabel = 'Write A Review';
	writeAReviewType = 'button';
	loadMoreLabel = '';
	starsOptions = {
		width: '20px',
		height: '20px'
	};
	rating = signal(0);
	count = signal(0);
	percentage = 0;

	displayRatingValue = computed(() => this.ratingValue() ?? this.rating());
	displayRatingCount = computed(() => this.ratingCount() ?? this.count());

	reviewsPaginated: ReviewsPaginationReturn;
	productId = '';
	isLoadMoreRequesting = false;
	mediaSource = MediaSourceEnum.DRAWER;
	searchFilter = '';
	selectedFilters: ReviewFilter[] = [];
	selectedSort = '';
	reviewsFilters = this.store.selectSignal(selectKillswitch('reviewsFilters'));
	reviewsProsCons = this.store.selectSignal(selectKillswitch('reviewsProsCons'));
	selectedTag = '';
	selectedFeature: SelectedProductReviewsProsCons;

	isLoading = false;
	selectProductId$ = this.store.select(selectProductReviewProductIdentifier);
	selectFilters$ = this.store.select(selectReviewFilters);
	selectSort$ = this.store.select(selectSortFilter);
	selectProConFeature$ = this.store.select(selectSelectedFeature);
	selectSearch$ = this.store.select(selectSearchFilter);
	selectReviewSummary$ = this.store
		.select(selectProductReviewSummary)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	selectReviews$ = this.store.select(selectProductReviews);
	shouldSaveReviewsAsDefault = false;
	isNewReviewsEnabled = this.store.selectSignal(selectKillswitch('isNewReviewsEnabled'));
	isReviewsAISummaryEnabled = this.store.selectSignal(
		selectKillswitch('isReviewsAISummaryEnabled')
	);
	isHighlyRatedReviewsEnabled = this.store.selectSignal(
		selectKillswitch('isHighlyRatedReviewsEnabled')
	);

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconStarFilled,
			homefieldIconStarHalf,
			homefieldIconStarOutlined
		]);
	}

	ngOnInit(): void {
		this.selectReviewSummary$.subscribe((reviewSummary: ReviewSummary) => {
			this.rating.set(reviewSummary.averageRating);
			this.count.set(reviewSummary.reviewCount);
			this.percentage = reviewSummary.recommendationPercentage;
		});

		this.setupReviewsStore$().subscribe(
			(reviewsPaginated) => {
				if (this.shouldSaveReviewsAsDefault) {
					this.store.dispatch(setProductReviews({ reviews: reviewsPaginated.reviews }));
					this.shouldSaveReviewsAsDefault = false;
				}

				if (reviewsPaginated.reviews) {
					this.loadMoreLabel = this.getLoadMoreLabel(reviewsPaginated.pagination);
					this.reviewsPaginated = { ...reviewsPaginated };
				}

				this.isLoadMoreRequesting = false;
			},
			() => {
				this.isLoadMoreRequesting = !this.isLoadMoreRequesting;
			}
		);
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	loadMoreReviews(): void {
		const { pagination } = this.reviewsPaginated;
		this.isLoadMoreRequesting = true;
		this.isLoading = true;

		this.requestReviews(pagination.to, pagination.perPage)
			.pipe(
				take(1),
				finalize(() => (this.isLoading = false))
			)
			.subscribe(
				(reviewsPaginated: ReviewsPaginationReturn) => {
					if (reviewsPaginated.reviews.length) {
						this.loadMoreLabel = this.getLoadMoreLabel(reviewsPaginated.pagination);

						this.reviewsPaginated = {
							...reviewsPaginated,
							reviews: [...this.reviewsPaginated.reviews, ...reviewsPaginated.reviews]
						};
					}
					this.isLoadMoreRequesting = !this.isLoadMoreRequesting;
				},
				() => {
					this.isLoadMoreRequesting = !this.isLoadMoreRequesting;
				}
			);
	}

	setupReviewsStore$(): Observable<ReviewsPaginationReturn> {
		return combineLatest([
			this.selectProductId$,
			this.selectReviews$,
			this.selectFilters$,
			this.selectSort$,
			this.selectSearch$,
			this.selectReviewSummary$,
			this.selectProConFeature$
		]).pipe(
			takeUntil(this.UNSUBSCRIBE$),
			debounceTime(300),
			filter(([productId]) => !!productId),
			tap(([, , filters, sortBy, searchFilter, , feature]) => {
				this.searchFilter = typeof searchFilter === 'string' ? searchFilter : '';
				this.selectedFilters = filters instanceof InvalidResponse ? [] : filters;
				this.selectedSort =
					sortBy instanceof InvalidResponse ? '' : `${sortBy.name}:${sortBy.value}`;
				this.selectedFeature = feature instanceof InvalidResponse ? null : feature;
				this.isLoading = true;
			}),
			switchMap(
				([
					productId,
					reviews,
					filters,
					sortBy,
					searchFilter,
					reviewSummary,
					proConFeature
				]) => {
					this.productId = productId;
					this.isLoadMoreRequesting = true;

					// If there is not any filter stored by PDP or Drawer return the 15 reviews already requested in PDP.
					if (
						filters instanceof InvalidResponse &&
						sortBy instanceof InvalidResponse &&
						searchFilter instanceof InvalidResponse &&
						proConFeature instanceof InvalidResponse
					) {
						const totalReviews =
							reviewSummary instanceof InvalidResponse
								? reviews.length
								: reviewSummary.reviewCount;

						if (this.isFirstPanelOpen && this.isNewReviewsEnabled()) {
							this.getReviewImages(productId);
							if (reviews.length < 15 && totalReviews >= 15) {
								this.shouldSaveReviewsAsDefault = true;
								return this.requestReviews();
							}
							this.isFirstPanelOpen = false;
						}

						const pagination = this.reviewService.createPagination(totalReviews, 0, 15);

						return of({ reviews, pagination });
					}

					// Else, if any filter is stored by PDP or Drawer then request new 15 reviews.
					return this.requestReviews();
				}
			),
			tap(() => (this.isLoading = false))
		);
	}

	getReviewImages(productIdentifier: string): void {
		this.reviewService
			.getReviewsMultimedia$(productIdentifier)
			.subscribe((reviewsMultimedia) => {
				if (reviewsMultimedia instanceof InvalidResponse) {
					return;
				}

				this.store.dispatch(addProductReviewsMultimedia({ reviewsMultimedia }));
			});
	}

	private requestReviews(offset = 0, limit = 15): Observable<ReviewsPaginationReturn> {
		const reviewsOptions = {
			offset,
			limitTo: limit,
			filter: this.selectedFilters,
			sortBy: this.selectedSort,
			search: this.searchFilter,
			feature: null
		};

		if (this.isHighlyRatedReviewsEnabled() && this.selectedFeature) {
			reviewsOptions.feature = this.selectedFeature.feature;
		}

		return this.reviewService.getReviewsResults$(this.productId, reviewsOptions);
	}

	private getLoadMoreLabel({
		currentPage,
		lastPage,
		secondLastPage,
		perPage,
		total,
		to
	}: ReviewPagination): string {
		let nextResults = perPage;

		if (currentPage === secondLastPage) {
			nextResults = total - to;
		} else if (currentPage === lastPage) {
			nextResults = 0;
		}

		return `Load ${nextResults} more reviews`;
	}
}
